export class Tiles {
	public constructor(elTiles: HTMLElement[], createFromExisting = false) {
		const debounce = (callback: TimerHandler, duration = 100) => {
			let timer: number;
			return (ev: UIEvent) => {
				clearTimeout(timer);
				timer = window.setTimeout(callback, duration, ev);
			};
		};

		for (const elTile of elTiles) {
			// identify corresponding elements
			// .tile .tile__layer--fg .tile__content .tile__content__text
			const elTileContent = elTile.querySelector(".tile__layer--fg .tile__content__text");
			if (elTileContent === null) {
				continue;
			}

			// calculate line elements
			// - When tiles used in a slider, the tiles get duplicated. A new Tiles object needs to be created for the
			//   duplicate tile. In this case, recreate the original text content form the generated SVG to run through
			//   the text scaling process again
			const elLines = createFromExisting
				? this.#getExistingTileLines(elTileContent)
				: this.#getTileLines(elTileContent);

			// clear the tile contents and replace with generated SVGs
			this.#setScaledLines(elTileContent, elLines);

			// redraw on window resize
			window.addEventListener("resize", debounce(() => this.#setScaledLines(elTileContent, elLines)));
		}
	}

	/**
	 *
	 */
	#getTileLines(elTileContent: Element): ChildNode[] {
		// identify and separate the HTML/Text nodes in the tile content area
		// - remove any <br> tags or empty text nodes
		return [...new DOMParser().parseFromString(elTileContent.innerHTML, "text/html").body.childNodes]
			.filter(line => {
				const outerHtml = line instanceof Element ? line.outerHTML : "";
				const textContent = line.textContent?.trim() ?? "";
				return (outerHtml || textContent) && !(/^<br(?:\s?\/)?>$/i.test(outerHtml || textContent));
			});
	}

	/**
	 *
	 */
	#getExistingTileLines(elTileContent: Element): ChildNode[] {
		return [...new DOMParser().parseFromString(elTileContent.innerHTML, "text/html").body.childNodes]
			.map(elLine => {
				return elLine instanceof SVGElement
					? document.createTextNode(elLine.childNodes[0]?.textContent ?? "")
					: elLine;
			});
	}

	/**
	 *
	 */
	#setScaledLines(elTileContent: Element, elLines: ChildNode[]) {
		// remove the original content of the tile
		while (elTileContent.firstChild) {
			elTileContent.removeChild(elTileContent.firstChild);
		}

		// create SVG elements for each line of regular text and scale the text to fit the container
		// - if text is wrapped in <span class="mini">...</span>, output the node directly
		const svgReplacements = [];
		for (const elLine of elLines) {
			// if the line is an HTML node, output it directly without creating an SVG replacement
			if (elLine instanceof Element && elLine.outerHTML) {
				elTileContent.appendChild(elLine);
				continue;
			}

			// create the svg text replacements and add to the page
			const elSvgText = document.createElementNS("http://www.w3.org/2000/svg", "text");
			elSvgText.setAttribute("fill", "currentColor");
			elSvgText.innerHTML = elLine.textContent ?? "";
			const elSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
			elSvg.appendChild(elSvgText);
			elTileContent.appendChild(elSvg);

			svgReplacements.push({
				node: elSvg,
				bbox: elSvgText.getBBox(),
			});
		}

		// identify the width of the widest line on the tile
		const maxLineWidth = Math.max(...svgReplacements.map(({ bbox }) => bbox.width));

		// scale each line to match the font size of the widest line
		for (const { node, bbox } of svgReplacements) {
			const x = bbox.x - (maxLineWidth - bbox.width) / 2; // move the viewBox left so smaller-width lines are in the center of the viewBox
			const y = bbox.y;                                   // y-position unchanged
			const width = maxLineWidth;                         // match the width of the widest line so font sizes will match on all lines
			const height = bbox.height;                         // height unchanged

			node.setAttribute("viewBox", [x, y, width, height].join(" "));
		};
	}
}
